import { useCallback, useEffect, useMemo } from 'react';
import { T, FONT } from './theme';
import { useDeckState, ACTS, PLAN_ACT_INDEX, type DetailsMode } from './useDeckState';
import { useSectionTimer } from './useSectionTimer';
import { HubBar } from './HubBar';
import { FooterNav } from './FooterNav';
import { ActOverview } from './acts/ActOverview';
import { ActDetails } from './acts/ActDetails';
import { ActMethod } from './acts/ActMethod';
import { ActPlan } from './acts/ActPlan';
import { ActResults } from './acts/ActResults';
import { ActNextSteps } from './acts/ActNextSteps';
import { ActClose } from './acts/ActClose';
import { ActDisclosures } from './acts/ActDisclosures';
import {
  buildCTA,
  buildCloseVM,
  buildDetailsVM,
  buildDisclosuresVM,
  buildNextStepsVM,
  buildOptimizations,
  buildOverviewVM,
  buildResultsVM,
} from '../formatters/deckContent';
import { trackEvent } from '../services/analytics';
import type { AdvisorMeta, ReportEnrichment, WebReportPayload } from '../types/webReport';

interface Props {
  slug: string;
  payload: WebReportPayload;
  enrichment: ReportEnrichment | null;
  advisor: AdvisorMeta | null;
  interactive?: boolean;
}

/** The interactive 7-act deck: HubBar + active act + FooterNav. */
export function DeckShell({ slug, payload, enrichment, advisor, interactive = true }: Props) {
  const optimizations = useMemo(() => buildOptimizations(payload, enrichment), [payload, enrichment]);
  const overviewVM = useMemo(() => buildOverviewVM(payload, enrichment, advisor), [payload, enrichment, advisor]);
  const detailsVM = useMemo(() => buildDetailsVM(payload, enrichment), [payload, enrichment]);
  const resultsVM = useMemo(() => buildResultsVM(enrichment, optimizations), [enrichment, optimizations]);
  const nextSteps = useMemo(() => buildNextStepsVM(optimizations), [optimizations]);
  const closeVM = useMemo(
    () => buildCloseVM(payload, enrichment, advisor, optimizations),
    [payload, enrichment, advisor, optimizations]
  );
  const disclosuresVM = useMemo(() => buildDisclosuresVM(payload, enrichment, advisor), [payload, enrichment, advisor]);
  const cta = useMemo(() => buildCTA(advisor), [advisor]);

  const { act, opt, detailsMode, setDetailsMode, go, setOpt, advance, goToDetailsEdit } = useDeckState(
    optimizations.length
  );

  // One tracking path for every way of landing on an optimization deep dive:
  // card click, dot switcher, footer Next / arrow keys.
  useEffect(() => {
    if (!interactive || opt === null) return;
    const o = optimizations[opt];
    if (o) trackEvent(slug, 'card_expanded', { cardId: o.key, category: o.category });
  }, [opt, interactive, optimizations, slug]);

  // Active section key for dwell-time tracking: the act id, except inside the
  // Plan act where an open deep-dive is keyed by its category (matching the
  // dashboard depth chart) and the strategy grid is 'plan'.
  const section = useMemo(() => {
    if (act === PLAN_ACT_INDEX) {
      if (opt !== null && optimizations[opt]) return optimizations[opt].category;
      return 'plan';
    }
    return ACTS[act]?.id ?? 'overview';
  }, [act, opt, optimizations]);

  useSectionTimer(section, slug, interactive);

  const openOptimization = useCallback((i: number) => setOpt(i), [setOpt]);

  const handleBook = useMemo(() => {
    const url = cta.bookUrl;
    if (!url) return null;
    return () => {
      if (interactive) trackEvent(slug, 'cta_clicked', { url });
      window.open(url, '_blank');
    };
  }, [cta.bookUrl, interactive, slug]);

  const handleRefine = useCallback(() => {
    if (interactive) trackEvent(slug, 'rerun_clicked', { cardId: 'details-edit' });
    goToDetailsEdit();
  }, [interactive, slug, goToDetailsEdit]);

  // Details' own Edit button routes through here so it's tracked too.
  const handleSetDetailsMode = useCallback(
    (m: DetailsMode) => {
      if (m === 'edit' && detailsMode !== 'edit' && interactive) {
        trackEvent(slug, 'rerun_clicked', { cardId: 'details-edit' });
      }
      setDetailsMode(m);
    },
    [detailsMode, interactive, slug, setDetailsMode]
  );

  const brandLabel = advisor?.company || advisor?.name || 'PlanScout';

  return (
    <div
      style={{
        fontFamily: FONT,
        background: T.bg,
        color: T.ink,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <HubBar act={act} go={go} />
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <div key={`${act}-${opt}`} style={{ animation: 'deckFade .45s ease', height: '100%' }}>
          {act === 0 && <ActOverview vm={overviewVM} />}
          {act === 1 && (
            <ActDetails
              vm={detailsVM}
              payload={payload}
              enrichment={enrichment}
              mode={detailsMode}
              setMode={handleSetDetailsMode}
              slug={slug}
              interactive={interactive}
            />
          )}
          {act === 2 && <ActMethod />}
          {act === 3 && (
            <ActPlan
              optimizations={optimizations}
              opt={opt}
              openOptimization={openOptimization}
              setOpt={setOpt}
              payload={payload}
              enrichment={enrichment}
              interactive={interactive}
            />
          )}
          {act === 4 && <ActResults vm={resultsVM} />}
          {act === 5 && <ActNextSteps steps={nextSteps} />}
          {act === 6 && <ActClose vm={closeVM} onRestart={() => go(0)} interactive={interactive} />}
          {act === 7 && <ActDisclosures vm={disclosuresVM} />}
        </div>
      </div>
      <FooterNav
        act={act}
        opt={opt}
        optCount={optimizations.length}
        go={go}
        advance={advance}
        brandLabel={brandLabel}
        bookLabel={cta.bookLabel}
        onBook={handleBook}
        onRefine={handleRefine}
      />
    </div>
  );
}
